import { useMutation, useQueryClient } from 'react-query';
import { COOKIES_CONFIG } from '@belong/common';
import { parseCookies, setCookie, destroyCookie } from 'nookies';
import type { ExternalLoginModel, LoginRecaptchaUserModel, RegisterUserModel } from 'src/api/models';
import {
  loginExternalService,
  loginService,
  registerService,
  forgotPasswordService,
  logoutService,
  resetPasswordService,
  setPasswordService,
} from 'src/api/users';
import { setJwtToken, clearJwtToken } from 'src/utils/api-client';
import { setJwtToken as setOctaneJwtToken, clearJwtToken as clearOctaneJwtToken } from 'src/utils/octane-client';
import { setEnhancedConvertionData } from 'src/utils/tracking';

const { JWT_TOKEN, REFRESH_TOKEN, PARTNER_KEY, REFERRAL_USER, REFERRAL_ID } = COOKIES_CONFIG;

export type LoginProviders = 'Facebook' | 'LinkedIn' | 'Google' | 'Apple';

export type UserLogin = {
  email: string;
  password: string;
};

export type ExternalLogin = {
  accessToken: string;
  loginProvider: string;
};

export type ForgotPasswordEmail = {
  email: string;
};

export type SetPassword = {
  code: string;
  email: string;
  password: string;
};

export const LoginProvider: Record<string, LoginProviders> = {
  Facebook: 'Facebook',
  LinkedIn: 'LinkedIn',
  Google: 'Google',
};

export function useLogin() {
  const { isLoading, mutateAsync } = useMutation(login);

  return {
    isLoginLoading: isLoading,
    login: mutateAsync,
  };
}

export function useExternalLogin() {
  const { isLoading, mutateAsync } = useMutation(externalLogin());

  return {
    isExternalLoginLoading: isLoading,
    externalLogin: mutateAsync,
  };
}

export function useRegister() {
  const { isLoading, mutateAsync } = useMutation(register);

  return {
    isRegisterLoading: isLoading,
    register: mutateAsync,
  };
}

export function useLogout() {
  const queryClient = useQueryClient();

  return useMutation(logout, {
    onSuccess: () => {
      queryClient.setQueryData('user', undefined);
    },
  });
}

export function useForgotPassword() {
  const queryClient = useQueryClient();

  return useMutation(forgotPasswordService, {
    onSuccess: () => {
      queryClient.invalidateQueries('user');
    },
  });
}

export function useResetPassword() {
  const queryClient = useQueryClient();

  return useMutation(resetPasswordService, {
    onSuccess: () => {
      queryClient.invalidateQueries('user');
    },
  });
}

export function useSetPassword() {
  return useMutation(setPasswordService);
}

async function register(registerData: RegisterUserModel) {
  const cookies = parseCookies();
  const partnerKey = cookies[PARTNER_KEY.name];

  const data = await registerService({ ...registerData, encodedPartnerKey: partnerKey });

  if (data.token) {
    setJwtToken(data.token);
    setOctaneJwtToken(data.token);
    setCookie(null, JWT_TOKEN.name, data.token, JWT_TOKEN.options);
    setCookie(null, REFRESH_TOKEN.name, data.refreshToken, REFRESH_TOKEN.options);
  }

  return data;
}

export async function login(user: LoginRecaptchaUserModel) {
  try {
    const { token, refreshToken } = await loginService(user);

    if (!token) {
      throw new Error();
    }

    setEnhancedConvertionData(user);
    setCookie(null, JWT_TOKEN.name, token, JWT_TOKEN.options);
    setCookie(null, REFRESH_TOKEN.name, refreshToken, REFRESH_TOKEN.options);
    setJwtToken(token);
    setOctaneJwtToken(token);
  } catch (error) {
    throw error;
  }
}

function externalLogin() {
  return async function (user: ExternalLoginModel) {
    try {
      const { token, refreshToken } = await loginExternalService(user);

      if (!token) {
        throw new Error();
      }

      setEnhancedConvertionData(user);
      setCookie(null, JWT_TOKEN.name, token, JWT_TOKEN.options);
      setCookie(null, REFRESH_TOKEN.name, refreshToken, REFRESH_TOKEN.options);
      setJwtToken(token);
      setOctaneJwtToken(token);
    } catch (error) {
      throw error;
    }
  };
}

export async function logout() {
  await logoutService();

  clearJwtToken();
  clearOctaneJwtToken();
  destroyCookie(null, JWT_TOKEN.name);
  destroyCookie(null, REFRESH_TOKEN.name);
  destroyCookie(null, REFERRAL_USER.name, { path: '/' });
  destroyCookie(null, REFERRAL_ID.name, { path: '/' });
  window.Intercom?.('shutdown');
}
